import React from 'react';
import { X, LayoutGrid, Sun, Kanban, Activity, Shield, Zap, Target } from 'lucide-react';
import { Button } from './Button';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  view: 'matrix' | 'kanban' | 'settings' | 'analytics' | 'focus' | 'daily';
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, view }) => {
  if (!isOpen) return null;

  const getContent = () => {
    switch (view) {
      case 'matrix':
        return {
          title: 'The Eisenhower Matrix',
          icon: <LayoutGrid className="text-blue-600" size={32} />,
          description: 'A decision-making tool to help you prioritize tasks by urgency and importance.',
          sections: [
            {
              title: '🔴 Do First (Urgent & Important)',
              text: 'Crises, deadlines, and pressing problems. Do these immediately.'
            },
            {
              title: '🔵 Schedule (Not Urgent & Important)',
              text: 'Strategic planning, exercise, skill building. This is where success happens. Don\'t neglect Q2!',
              highlight: true
            },
            {
              title: '🟡 Delegate (Urgent & Not Important)',
              text: 'Interruptions, some emails/calls. Things that seem urgent but don\'t advance your goals.'
            },
            {
              title: '🟢 Eliminate (Neither)',
              text: 'Time wasters and busy work. Delete these.'
            }
          ]
        };
      case 'daily':
        return {
          title: 'Daily Plan & Rituals',
          icon: <Sun className="text-orange-500" size={32} />,
          description: 'Separate your recurring habits from your daily wins.',
          sections: [
            {
              title: '🔄 Daily Rituals',
              text: 'Habits you want to build (e.g., "Read 10 pages", "Workout"). These tasks reset every day automatically. Consistency is key.'
            },
            {
              title: '🏆 Accomplished Today',
              text: 'A log of everything you completed today, whether it came from your Matrix or your Rituals. Watch this list grow for a dopamine boost!'
            }
          ]
        };
      case 'kanban':
        return {
          title: 'Kanban Board',
          icon: <Kanban className="text-purple-600" size={32} />,
          description: 'Visualize your workflow and limit work-in-progress.',
          sections: [
            {
              title: 'Focus on Flow',
              text: 'Move tasks from left to right. Try to keep the "Doing" column small to avoid context switching.'
            },
            {
              title: 'Syncs with Matrix',
              text: 'This view shows the same tasks as your Matrix, just organized by Status instead of Priority.'
            }
          ]
        };
      case 'focus':
        return {
          title: 'Deep Focus Mode',
          icon: <Zap className="text-yellow-500" size={32} />,
          description: 'Eliminate distractions and enter flow state.',
          sections: [
            {
              title: '🛡️ Site Blocking (Pro)',
              text: 'When the timer starts, distracting websites defined in Settings are blocked automatically.'
            },
            {
              title: '🎵 Soundscapes',
              text: 'Use white noise or ambient sounds to mask background noise and improve concentration.'
            }
          ]
        };
      default:
        return {
          title: 'Productivity Pulse',
          icon: <Activity className="text-green-600" size={32} />,
          description: 'Track your performance and gamification progress.',
          sections: [
            {
              title: 'XP & Levels',
              text: 'Earn XP by completing tasks and finishing focus sessions. Leveling up unlocks new themes and sounds.'
            },
            {
              title: 'Matrix Harmony',
              text: 'Check if you are spending too much time in "Firefighting" mode (Q1) or "Strategy" mode (Q2).'
            }
          ]
        };
    }
  };

  const content = getContent();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-200 dark:border-dark-border flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-start bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-dark-bg rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              {content.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{content.title}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{content.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          {content.sections.map((section, idx) => (
            <div key={idx} className={`relative pl-4 ${section.highlight ? 'bg-blue-50 dark:bg-blue-900/20 -mx-4 px-8 py-4 rounded-lg' : ''}`}>
              {!section.highlight && (
                 <div className="absolute left-0 top-1.5 w-1 h-4 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
              )}
              <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-2">
                {section.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {section.text}
              </p>
            </div>
          ))}
          
          <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg flex gap-3 items-start">
             <Target className="text-gray-500 shrink-0 mt-0.5" size={18} />
             <div>
                <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Pro Tip</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                  "What is important is seldom urgent, and what is urgent is seldom important." - Dwight Eisenhower
                </p>
             </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-end">
          <Button onClick={onClose}>Got it</Button>
        </div>

      </div>
    </div>
  );
};
